import { isBefore, isSameMonth } from 'date-fns';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';

import type { TLookupShip } from '@/ducks/common/lookup/types';
import type { TQuickFindLink } from '@/features/quickFindFilters/types';
import type { Package } from '@/infra/types/voyageInfo/package';
import type { Port } from '@/infra/types/voyageInfo/port';
import type { SailingFromPackages } from '@/infra/types/voyageInfo/sailing';

import { FILTER_KEY } from '@/ducks/filters/types';
import { getSearchParamsValue } from '@/ducks/filters/utils/primitive';
import { getQFAdditionalParams } from '@/features/quickFindFilters/helpers';
import { checkForValueType, ensureArray } from '@/helpers/util';

export const getPackagesForFilteredSailings = (filteredSailings: SailingFromPackages[], packages: Package[]) => {
  const groupedSailings: Record<string, SailingFromPackages[]> = groupBy(uniqBy(filteredSailings, 'id'), 'packageCode');

  return packages.reduce<Package[]>(
    (packages, pckg) =>
      groupedSailings[pckg.packageCode]
        ? [...packages, { ...pckg, sailingList: groupedSailings[pckg.packageCode]! }]
        : packages,
    [],
  );
};

export const getSailingsFromPackages = (packages: Package[]) => {
  if (!checkForValueType(packages)) {
    return [];
  }

  const sailings: SailingFromPackages[] = [];
  packages.forEach((pckg) => {
    pckg.sailingList.forEach((sailing) => {
      sailings.push(sailing);
    });
  });

  return sailings;
};

export const filterShips = (packages: Package[], shipList: TLookupShip[]) => {
  if (isEmpty(shipList) || shipList === undefined) {
    return packages;
  }
  const sailings = getSailingsFromPackages(packages);
  if (!checkForValueType(sailings)) {
    return packages;
  }

  const filteredSailings = sailings.filter((s) => shipList.some((ship) => s.ship.code === ship?.id));
  return getPackagesForFilteredSailings(filteredSailings, packages);
};

export const filterDeparturePorts = (packages: Package[], homePorts: Port[]) => {
  if (isEmpty(homePorts) || homePorts === undefined) {
    return packages;
  }
  const finalpackages = packages?.filter((s) => homePorts?.some((port) => s?.departurePortCode === port?.code));
  return finalpackages;
};

export const filterByPromoVoyages = (voyages: string[], packages: Package[]) => {
  if (isEmpty(packages) || isEmpty(voyages)) {
    return packages;
  }

  const filteredPackages = packages.reduce<Package[]>((packages, pack) => {
    const sailings = pack.sailingList.filter((sailing) =>
      ensureArray(voyages)?.some((voyageId) => voyageId === sailing?.id),
    );
    return !isEmpty(sailings)
      ? [
          ...packages,
          {
            ...pack,
            sailingList: sailings,
          },
        ]
      : packages;
  }, []);

  return filteredPackages;
};

export const filterQFLink = (
  packages: Package[],
  QFLink: TQuickFindLink,
  itinerariesRegionsMap: Record<string, string[]>,
  portsRegionsMap: Record<string, string[]>,
) => {
  if (!QFLink) {
    return packages;
  }
  let filteredPackages = packages;

  const reducePackages = (packages: Package[], condition: (sailing: SailingFromPackages) => boolean) => {
    return packages.reduce<Package[]>((acc, pack) => {
      const sailings = pack.sailingList.filter(condition);
      if (isEmpty(sailings)) {
        return acc;
      }
      return [
        ...acc,
        {
          ...pack,
          sailingList: sailings,
        },
      ];
    }, []);
  };

  const QFAdditionalParams = getQFAdditionalParams(QFLink);

  const QFDateFrom = getSearchParamsValue(FILTER_KEY.dateFrom, QFAdditionalParams);
  if (QFDateFrom) {
    filteredPackages = reducePackages(
      filteredPackages,
      (sailing) =>
        isBefore(QFDateFrom as string, sailing.startDate) || isSameMonth(QFDateFrom as string, sailing.endDate),
    );
  }

  const QFDateTo = getSearchParamsValue(FILTER_KEY.dateTo, QFAdditionalParams);
  if (QFDateTo) {
    filteredPackages = reducePackages(filteredPackages, (sailing) => isBefore(sailing.endDate, QFDateTo as string));
  }

  const QFItinerariesRegions = ensureArray(getSearchParamsValue(FILTER_KEY.destPackagesRegions, QFAdditionalParams));
  const QFItineraries = ensureArray(getSearchParamsValue(FILTER_KEY.destPackages, QFAdditionalParams));
  if (QFItinerariesRegions?.length || QFItineraries?.length) {
    filteredPackages = filteredPackages.filter((pack) =>
      (QFItinerariesRegions ?? []).some(
        (QFRegion) =>
          itinerariesRegionsMap[pack.packageCode]?.includes(QFRegion as string) &&
          (!QFItineraries?.length || QFItineraries?.some((itinerary) => itinerary === pack.packageCode)),
      ),
    );
  }

  const QFPortsRegions = ensureArray(getSearchParamsValue(FILTER_KEY.destPortsRegions, QFAdditionalParams));
  const QFPorts = ensureArray(getSearchParamsValue(FILTER_KEY.destPorts, QFAdditionalParams));
  if (QFPortsRegions?.length || QFPorts?.length) {
    filteredPackages = reducePackages(filteredPackages, (sailing) =>
      sailing.ports.some((currentPort) =>
        (QFPortsRegions ?? []).some(
          (QFRegion) =>
            portsRegionsMap[currentPort.code]?.includes(QFRegion as string) &&
            (!QFPorts?.length || QFPorts?.some((port) => port === currentPort.code)),
        ),
      ),
    );
  }

  return filteredPackages;
};
